import { AddIcon, AcceptIcon, ClearIcon } from "@fluentui/react-icons-mdl2"
import { Label } from "@fluentui/react/lib/Label"
import { Stack, StackItem } from "@fluentui/react/lib/Stack"
import { registerIcons, IProcessedStyleSet } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React from "react"

import {
    ContainerTO,
    ParameterTO,
    Constants as RestApiConstants
} from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"
import {
    ComponentFactory,
    Constants as ReactConfiguratorConstants
} from "@encoway/react-configurator"

import { ComponentName, VIEWPORT_PROPERTY_VALUE } from "../../constants"
import {
    BurgerMenuData,
    BurgerMenuHandle
} from "../BurgerMenu/BurgerMenu.types"
import { ParameterProps } from "../Parameter/Parameter.types"
import { SectionStyles } from "./Section.styles"
import {
    ISectionStyles,
    ParameterMenu,
    ParameterMenuItem,
    SectionProps
} from "./Section.types"

const REVERT_DECISION = "RevertDecision"
const OPTIONAL_POSITION = "OptionalPosition"
const ACCEPT = "Accept"
const CLEAR = "Clear"
registerIcons(
    {
        icons: {
            [REVERT_DECISION]: (
                /** Use Icon encoway-icons_reset.svg from https://encoway.atlassian.net/wiki/spaces/CPQJOBS/pages/32943074/Style-+Interaktions-Guide+und+Mockups+Modern+Sales+UI  */
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 2046.61 2046.61"
                    style={{ verticalAlign: "middle" }}
                    focusable="false"
                >
                    <path d="m1157.41,128.6H257.23V0h900.17c71,0,139.32,9.2,204.96,27.64,65.64,18.41,127.09,44.38,184.35,77.87,57.28,33.49,109.35,73.68,156.24,120.57,46.89,46.89,87.08,98.96,120.57,156.21,33.49,57.26,59.46,118.73,77.87,184.37,18.41,65.64,27.61,133.96,27.61,204.96,0,103.82-19.4,202.46-58.25,295.88-38.85,93.43-94.77,176.66-167.8,249.69-72.98,72.98-156.21,128.93-249.64,167.77-93.43,38.85-192.06,58.27-295.91,58.27H332.62s427.96,429.03,427.96,429.03l-74.35,74.35L117.61,1478.94l568.62-567.65,74.35,74.35-427.96,429h824.78c58.94,0,115.9-7.72,170.83-23.12,54.92-15.41,106.18-37.01,153.71-64.8,47.56-27.78,90.92-61.28,130.09-100.47,39.2-39.2,72.69-82.56,100.47-130.12,27.81-47.56,49.42-98.78,64.82-153.71,15.41-54.92,23.1-111.86,23.1-170.8s-7.69-115.88-23.1-170.8c-15.41-54.92-37.01-106.18-64.82-153.73-27.78-47.56-61.28-90.92-100.47-130.09-39.17-39.2-82.54-72.69-130.09-100.47-47.53-27.81-98.78-49.42-153.71-64.82-54.92-15.41-111.88-23.1-170.83-23.1Z" />
                </svg>
            ),
            [OPTIONAL_POSITION]: <AddIcon />,
            [ACCEPT]: <AcceptIcon />,
            [CLEAR]: <ClearIcon />
        }
    },
    { disableWarnings: true }
)

interface BurgerMenuControllerProps {
    handles: React.RefObject<BurgerMenuHandle>[]
}

const BurgerMenuController: (p: BurgerMenuControllerProps) => null = (
    props: BurgerMenuControllerProps
) => {
    const { handles } = props
    React.useEffect(() => {
        const foundActiveItem =
            handles.findIndex((h) => h.current?.hasActiveItems()) >= 0
        handles.forEach((h) => h.current?.allowPlaceholder(foundActiveItem))
    })
    return null
}

const toChild = (container: ContainerTO, props: Record<string, unknown>) => {
    return ComponentFactory.instanceOf(container.viewPort || "", {
        key: container.id,
        data: container,
        ...props
    })
}
interface PM extends BurgerMenuData, ParameterMenu {}

interface ExtendedParameterProps extends ParameterProps {
    parameter: ParameterTO
    burgerMenuHandles: React.RefObject<BurgerMenuHandle>[]
}

const ParamComponent = (props: ExtendedParameterProps) => {
    const { parameter, burgerMenuHandles, ...paramProps } = props
    const menuRef = React.useRef<BurgerMenuHandle>()
    const parameterMenuItems: ParameterMenuItem[] = []
    const menuData: PM = {
        add: (item: ParameterMenuItem) => add(parameterMenuItems, item),
        remove: (item: ParameterMenuItem) =>
            remove(parameterMenuItems, item.key),
        getItems: () => parameterMenuItems,
        setMenuHandle: (h: BurgerMenuHandle) => {
            menuRef.current = h
        }
    }
    burgerMenuHandles.push(menuRef as React.RefObject<BurgerMenuHandle>)
    parameterMenuItems.splice(0, parameterMenuItems.length)
    if (
        parameter.undoable &&
        parameter.viewPortProperties?.ALLOW_UNDO ===
            VIEWPORT_PROPERTY_VALUE.True
    ) {
        menuData.add(UndoDecisionItem(parameter, props))
    }

    const factory = () =>
        ComponentFactory.instanceOf(parameter.viewPort || "", {
            ...paramProps,
            key: parameter.id,
            data: parameter,
            parameterMenuData: menuData
        })

    if (
        parameter.viewPortProperties?.OPTIONAL_POSITIONS ===
        VIEWPORT_PROPERTY_VALUE.True
    ) {
        return (
            <OptionalPositionDisplay
                key={parameter.id}
                factory={factory}
                parameterMenuData={menuData}
                data={parameter}
                onValueChanged={props.onValueChanged}
                mediaLink={props.mediaLink}
                options={props.options}
            />
        )
    }

    return factory()
}

const OptionalPositionDisplay = (props: Record<string, unknown>) => {
    return ComponentFactory.instanceOf(
        ComponentName.OptionalPositionDisplay,
        props
    )
}

const UndoDecisionItem = (parameter: ParameterTO, props: ParameterProps) => {
    return {
        key: "undoDecision",
        iconProps: { iconName: REVERT_DECISION },
        text: L10n.format("Configuration.Undo.label"),
        onClick: () => {
            props.onValueChanged &&
                props.onValueChanged(
                    parameter,
                    ReactConfiguratorConstants.Undo,
                    RestApiConstants.ValueFormat.Unformatted
                )
        },
        canCheck: false,
        order: 0
    }
}

const remove = (items: ParameterMenuItem[], key: string) => {
    const index = items.findIndex((i: ParameterMenuItem) => i.key === key)
    if (index >= 0) {
        items.splice(index, 1)
    }
}

const add = (items: ParameterMenuItem[], item: ParameterMenuItem) => {
    remove(items, item.key)
    items.splice(items.length, 0, item)
    items.sort(compare)
}

const compare = (a: ParameterMenuItem, b: ParameterMenuItem) => {
    const aOrder = a.order
    const bOrder = b.order
    if (aOrder === bOrder) {
        return 0
    } else if (bOrder === undefined) {
        return -1
    } else if (aOrder === undefined) {
        return 1
    }
    return aOrder < bOrder ? -1 : 1
}

interface ExtendedSectionProps extends SectionProps {
    /**
     * Stores the BurgerMenu handles displayed in the Section.
     */
    burgermenuHandles?: React.RefObject<BurgerMenuHandle>[]
    /**
     * Data information displayed in the Root component.
     */
    rootDataId?: string
}

/**
 * The Section component renders the title of a ContainerTO and all its nested ContainerTOs and ParameterTOs.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Section/Section.tsx)
 * - [SectionStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Section/Section.styles.ts)
 * - [SectionProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Section/Section.types.ts)
 * - [MS Fluent Stack](https://developer.microsoft.com/de-DE/fluentui#/controls/web/stack)
 *
 * @visibleName Section
 */
function ISection(props: ExtendedSectionProps) {
    const {
        styles,
        theme,
        data,
        hideLabel,
        shouldDisplayLabel,
        onRenderLabel,
        ...delegatedProps
    } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<ISectionStyles>

    if (!data) {
        return null
    }
    const burgermenuHandles = props.burgermenuHandles || []
    const rootDataId = props.rootDataId || props.data.id
    const defaultGetLabel = (containerTO: ContainerTO) => {
        const label = containerTO.translatedName
        if (props.prefix) {
            return `${props.prefix} - ${label}`
        }
        return label
    }

    const getLabel = (containerTO: ContainerTO) =>
        props.onGetLabel
            ? props.onGetLabel(containerTO, defaultGetLabel)
            : defaultGetLabel(containerTO)

    const defaultOnRenderLabel = (label?: string) => {
        return <Label className={classNames.label}>{label}</Label>
    }

    const shouldDisplayLabelDefault = (containerTO: ContainerTO) => {
        return containerTO.parameters?.length > 0
    }

    const testShouldDisplayLabel = shouldDisplayLabel
        ? (containerTO: ContainerTO) =>
              shouldDisplayLabel(containerTO, shouldDisplayLabelDefault)
        : shouldDisplayLabelDefault
    const label = getLabel(data)
    const showLabel = !hideLabel && label && testShouldDisplayLabel(data)
    const isEmptyLabel = label && !/\S/.test(label)

    const containerProps = {
        prefix: hideLabel ? undefined : !isEmptyLabel && label,
        ...delegatedProps,
        rootDataId,
        burgermenuHandles
    }

    return (
        <Stack className={classNames.root}>
            {showLabel && !isEmptyLabel ? (
                <StackItem className={classNames.labelContainer}>
                    {onRenderLabel
                        ? onRenderLabel(label, defaultOnRenderLabel)
                        : defaultOnRenderLabel(label)}
                </StackItem>
            ) : null}
            <StackItem className={classNames.parameters}>
                {data.parameters.map((p) => (
                    <ParamComponent
                        key={p.id}
                        parameter={p}
                        burgerMenuHandles={burgermenuHandles}
                        {...(delegatedProps as ParameterProps)}
                    />
                ))}
            </StackItem>
            <StackItem className={classNames.containers}>
                {data.children.map((c) => toChild(c, containerProps))}
            </StackItem>
            {props.burgermenuHandles ? null : (
                <BurgerMenuController handles={burgermenuHandles} />
            )}
        </Stack>
    )
}

/**
 * The Section component renders the title of a ContainerTO and all its nested ContainerTOs and ParameterTOs.
 *
 * @see SectionStyles
 * @see SectionProps
 */
export const Section = styled(ISection, SectionStyles)
Section.displayName = "Section"
